import { useContext, useEffect, useRef, useState } from "react";

import { BTButton } from "@btnet/core-components/BTButton";
import {
    useMfaEnrollmentStateGetV2,
    useMfaEnrollmentStateNudgesPostV2,
} from "@btnet/services-btnet/handlers/mfa-enrollment-state/mfa-enrollment-state";

import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { externalPages } from "utilities/externalPages";

import { BTAlert } from "commonComponents/btWrappers/BTAlert/BTAlert";
import { BTIconLockFilled, BTIconLockOutlined } from "commonComponents/btWrappers/BTIcon";
import { withErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";

import "./MfaEnrollmentNudgeBanner.less";

import { getMfaNudgeTierMeta } from "./MfaEnrollmentNudgeBanner.constants";
import {
    dismissMfaNudge,
    getMfaNudgeStoredState,
    markMfaNudgeNotNeeded,
    markMfaNudgeShown,
} from "./MfaEnrollmentNudgeBanner.storage";

interface IMfaEnrollmentNudgeBannerProps {
    onLearnMoreClick?: () => void;
    onEnrollClick?: () => void;
}

const MfaEnrollmentNudgeBannerInternal: React.FC<IMfaEnrollmentNudgeBannerProps> = ({
    onLearnMoreClick,
    onEnrollClick,
}) => {
    const userInfo = useContext(UserInfoContext);
    const userId = userInfo?.userId ?? null;

    // Recompute on every render rather than freezing in a useState initializer: userId is null on
    // the first render while UserInfoContext hydrates, so a lazy-init read would gate the GET on a
    // null user forever and re-fire on every reload despite a valid marker existing.
    const storedState = getMfaNudgeStoredState(userId);
    const hasValidMarker = storedState !== null;

    const { data: response, isSuccess } = useMfaEnrollmentStateGetV2({
        query: { enabled: userId != null && !hasValidMarker },
    });
    const { mutate: recordImpression } = useMfaEnrollmentStateNudgesPostV2();
    const hasRecordedImpression = useRef(false);
    const [dismissed, setDismissed] = useState(false);

    const enrollmentState = response?.data;
    const shouldNudgeFromApi = enrollmentState?.isApplicable && enrollmentState?.shouldNudge;

    useEffect(() => {
        if (shouldNudgeFromApi && enrollmentState && userId != null) {
            markMfaNudgeShown(Number(enrollmentState.graceDaysRemaining ?? 0), userId);
        }
    }, [shouldNudgeFromApi, enrollmentState, userId]);

    useEffect(() => {
        if (isSuccess && !shouldNudgeFromApi && userId != null) {
            // Record the observed forceMfa so the block screen can tell a "nothing needed" marker
            // from a "blockable but not nudged" one and never suppress a legitimate block.
            markMfaNudgeNotNeeded(userId, Boolean(enrollmentState?.forceMfa));
        }
    }, [isSuccess, shouldNudgeFromApi, userId, enrollmentState]);

    useEffect(() => {
        if (shouldNudgeFromApi && !hasRecordedImpression.current) {
            hasRecordedImpression.current = true;
            recordImpression();
        }
    }, [shouldNudgeFromApi, recordImpression]);

    const shouldShow =
        shouldNudgeFromApi || (storedState?.wasShown === true && !storedState.dismissed);

    if (!shouldShow || dismissed) {
        return null;
    }

    const graceDaysRemaining =
        storedState?.wasShown === true && !storedState.dismissed
            ? storedState.graceDaysRemaining
            : Number(enrollmentState?.graceDaysRemaining ?? 0);
    const tier = getMfaNudgeTierMeta(graceDaysRemaining);
    const icon = tier.alertType === "warning" ? <BTIconLockFilled /> : <BTIconLockOutlined />;

    return (
        <BTAlert
            data-testid="mfaEnrollmentNudgeBanner"
            type={tier.alertType}
            title={tier.title}
            message={tier.body}
            banner
            closable
            onClose={() => {
                if (userId != null) {
                    dismissMfaNudge(userId);
                }
                setDismissed(true);
            }}
            showIcon
            icon={icon}
            action={
                <div
                    data-testid="mfaEnrollmentNudgeBanner-actions"
                    className="mfa-enrollment-nudge-banner-actions"
                >
                    <BTButton
                        data-testid="mfaEnrollmentNudgeBanner-learnMore"
                        type="secondary"
                        onClick={() => {
                            window.open(
                                externalPages.XeroMFAServiceCloudHelpDoc,
                                "_blank",
                                "noopener,noreferrer",
                            );
                            onLearnMoreClick?.();
                        }}
                    >
                        {tier.moreInfoLabel}
                    </BTButton>
                    <BTButton
                        data-testid="mfaEnrollmentNudgeBanner-enroll"
                        type="primary"
                        onClick={onEnrollClick}
                    >
                        {tier.ctaLabel}
                    </BTButton>
                </div>
            }
        />
    );
};

export const MfaEnrollmentNudgeBanner = withErrorBoundary(MfaEnrollmentNudgeBannerInternal)(
    "Could not load MFA Enrollment Nudge Banner",
);

export default MfaEnrollmentNudgeBanner;
